import { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  AddressElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  fetchQuote,
  createPaymentIntent,
  formatCents,
} from '../lib/orders-api';
import { getUserEmail } from '../lib/auth';
import type { ShippingAddress, PriceBreakdown } from '../lib/orders-api';

const stripePromise = loadStripe(
  (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ?? ''
);

interface OrderModalProps {
  open: boolean;
  onClose: () => void;
  bookTitle: string;
  bookHtml: string;
}

type Step = 'binding-address' | 'review-pay' | 'confirmation';

function PaymentForm({
  breakdown,
  bindingType,
  onSuccess,
  onError,
}: {
  breakdown: PriceBreakdown;
  bindingType: 'softcover' | 'hardcover';
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setPaymentError(null);
    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    setLoading(false);
    if (error) {
      const msg = error.message ?? 'Payment failed. Please try again.';
      setPaymentError(msg);
      onError(msg);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 text-sm space-y-1">
        <div className="flex justify-between font-semibold">
          <span>Total to charge</span>
          <span>{formatCents(breakdown.totalCents)}</span>
        </div>
        <p className="text-xs text-stone-400 capitalize">{bindingType} binding · ships to your address</p>
      </div>

      <PaymentElement />

      {paymentError && <div className="text-red-600 text-sm">{paymentError}</div>}

      <button
        type="submit"
        disabled={loading || !stripe}
        className="w-full py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
      >
        {loading ? 'Processing...' : `Pay ${formatCents(breakdown.totalCents)}`}
      </button>
    </form>
  );
}

export function OrderModal({ open, onClose, bookTitle }: OrderModalProps) {
  const [step, setStep] = useState<Step>('binding-address');
  const [bindingType, setBindingType] = useState<'softcover' | 'hardcover'>('softcover');
  const [address, setAddress] = useState<ShippingAddress | null>(null);
  const [breakdown, setBreakdown] = useState<PriceBreakdown | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced quote fetch — triggers on bindingType or address change
  useEffect(() => {
    if (!address) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const result = await fetchQuote({ bindingType, shippingAddress: address });
        setBreakdown(result);
      } catch {
        setQuoteError('Could not calculate price. Please check your address.');
      } finally {
        setQuoteLoading(false);
      }
    }, 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [bindingType, address]);

  // Create PaymentIntent when entering review-pay step
  useEffect(() => {
    if (step !== 'review-pay' || clientSecret || !breakdown || !address) return;
    setIntentLoading(true);
    setIntentError(null);
    getUserEmail()
      .catch(() => undefined)
      .then((userEmail) =>
        createPaymentIntent({ bindingType, shippingAddress: address, bookTitle, breakdown, userEmail })
      )
      .then(({ clientSecret: cs, orderId: oid }) => {
        setClientSecret(cs);
        setOrderId(oid);
      })
      .catch(() => setIntentError('Could not start payment. Please go back and try again.'))
      .finally(() => setIntentLoading(false));
  }, [step]);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step !== 'confirmation') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [step]);

  const handleClose = () => {
    setStep('binding-address');
    setBindingType('softcover');
    setAddress(null);
    setBreakdown(null);
    setQuoteLoading(false);
    setQuoteError(null);
    setClientSecret(null);
    setOrderId(null);
    setIntentLoading(false);
    setIntentError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        aria-hidden="true"
        onClick={step !== 'confirmation' ? handleClose : undefined}
      />

      {/* Modal panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Order Printed Book"
        className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl max-w-lg mx-auto overflow-y-auto max-h-[90dvh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="text-lg font-serif text-stone-900">Order Printed Book</h2>
          {step !== 'confirmation' && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="text-stone-400 hover:text-stone-600 text-2xl leading-none"
            >
              ×
            </button>
          )}
        </div>

        {/* Step indicator */}
        {step !== 'confirmation' && (
          <div className="px-6 pt-3 pb-1 flex gap-2">
            {(['binding-address', 'review-pay'] as const).map((s, i) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full ${
                  s === step || (i === 0 && step === 'review-pay')
                    ? 'bg-amber-500'
                    : 'bg-stone-200'
                }`}
              />
            ))}
          </div>
        )}

        {/* Step content */}
        <div className="px-6 py-4">
          {/* Step 1: Binding & Address */}
          {step === 'binding-address' && (
            <div className="space-y-5">
              {/* Binding picker */}
              <div>
                <p className="text-sm font-semibold text-stone-700 mb-2">Binding type</p>
                <div className="flex gap-3">
                  {(['softcover', 'hardcover'] as const).map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setBindingType(type)}
                      className={`flex-1 rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                        bindingType === type
                          ? 'border-amber-500 bg-amber-50 text-stone-900'
                          : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'
                      }`}
                    >
                      <div className="font-semibold capitalize">{type}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {type === 'softcover'
                          ? 'Perfect-bound, matte cover'
                          : 'Cloth-bound, premium feel'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Shipping address */}
              <div>
                <p className="text-sm font-semibold text-stone-700 mb-2">Shipping address</p>
                <Elements stripe={stripePromise}>
                  <AddressElement
                    options={{
                      mode: 'shipping',
                      allowedCountries: ['US'],
                      fields: { phone: 'never' },
                      display: { name: 'full' },
                    }}
                    onChange={e => {
                      if (e.complete) {
                        const a = e.value.address;
                        setAddress({
                          name: e.value.name,
                          line1: a.line1,
                          city: a.city,
                          state: a.state,
                          postalCode: a.postal_code,
                          country: a.country,
                        });
                      } else {
                        setAddress(null);
                        setBreakdown(null);
                      }
                    }}
                  />
                </Elements>
              </div>

              {/* Price breakdown */}
              {quoteLoading && (
                <div className="text-stone-400 text-sm text-center py-3">
                  Calculating price...
                </div>
              )}
              {quoteError && <div className="text-red-600 text-sm">{quoteError}</div>}
              {breakdown && !quoteLoading && (
                <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-600">
                      Book printing ({bindingType})
                    </span>
                    <span>{formatCents(breakdown.printCostCents)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Standard shipping</span>
                    <span>{formatCents(breakdown.shippingCostCents)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Lulu processing fee</span>
                    <span>{formatCents(breakdown.luluFeeCents)}</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-stone-200 pt-2">
                    <span>Total</span>
                    <span>{formatCents(breakdown.totalCents)}</span>
                  </div>
                  <p className="text-xs text-stone-400 pt-1">
                    Estimated delivery: 3–5 business days after order
                  </p>
                </div>
              )}

              <button
                type="button"
                disabled={!address || !breakdown || quoteLoading}
                onClick={() => setStep('review-pay')}
                className="w-full py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
              >
                Continue to Payment
              </button>
            </div>
          )}

          {/* Step 2: Review & Pay */}
          {step === 'review-pay' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => {
                  setClientSecret(null);
                  setStep('binding-address');
                }}
                className="text-sm text-stone-500 hover:text-stone-700 flex items-center gap-1"
              >
                ← Back
              </button>

              {intentLoading && (
                <div className="text-stone-400 text-sm text-center py-6">
                  Preparing payment...
                </div>
              )}
              {intentError && (
                <div className="text-red-600 text-sm">{intentError}</div>
              )}
              {clientSecret && breakdown && (
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret, appearance: { theme: 'stripe' } }}
                >
                  <PaymentForm
                    breakdown={breakdown}
                    bindingType={bindingType}
                    onSuccess={() => setStep('confirmation')}
                    onError={() => {}}
                  />
                </Elements>
              )}
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirmation' && (
            <div className="text-center space-y-4 py-6">
              <div className="text-4xl">📖</div>
              <h2 className="text-2xl font-serif text-stone-900">Your book is on its way!</h2>
              <p className="text-stone-600 text-sm">
                Order #{orderId?.slice(-8)} has been placed.
                <br />
                You'll receive a confirmation email shortly.
              </p>
              <p className="text-xs text-stone-400">
                Estimated delivery: 3–5 business days after printing begins.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="mt-4 px-6 py-2 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors"
              >
                Back to My Book
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
